/**
 * Entity source backed by the HeadLight API, read as the signed-in user.
 *
 * Reads include soft-deleted rows (`includeDeleted=true`, honoured by the API's read routes), so a
 * scan of a label whose record was deleted can say so instead of "not found".
 */

/**
 * @typedef {Object} EntityRequest
 * @property {import('headlight-qrcode/source/Headlight-QRCode-EntityRegistry.js').EntityDefinition} Definition
 * @property {'qr'|'guid'|'id'} Key
 * @property {string} Value
 * @property {boolean} [IncludeDeleted]
 */

class RestEntitySource
{
	/**
	 * @param {Object} pOptions
	 * @param {(pURL: string) => Promise<any>} pOptions.getJSON - Reads an API path relative to /1.0/, e.g. HeadlightRestClient.getJSON.
	 * @param {number} [pOptions.MaxRows] - Rows read per lookup (duplicates beyond this are not seen). Default 10.
	 */
	constructor(pOptions)
	{
		this.getJSON = pOptions.getJSON;
		this.maxRows = pOptions.MaxRows || 10;
	}

	/**
	 * @param {EntityRequest} pRequest
	 * @return {Promise<Array<Record<string, any>>>}
	 */
	async find(pRequest)
	{
		const tmpDefinition = pRequest.Definition;
		const tmpDeleted = pRequest.IncludeDeleted ? '?includeDeleted=true' : '';
		if (pRequest.Key === 'id')
		{
			const tmpRecord = await this.getJSON(`${tmpDefinition.Entity}/${encodeURIComponent(pRequest.Value)}${tmpDeleted}`);
			return (tmpRecord && !tmpRecord.Error && tmpRecord[tmpDefinition.IDColumn]) ? [ tmpRecord ] : [];
		}
		const tmpColumn = (pRequest.Key === 'guid') ? tmpDefinition.GUIDColumn : tmpDefinition.Column;
		if (!tmpColumn)
		{
			return [];
		}
		const tmpFilter = `FBV~${tmpColumn}~EQ~${encodeURIComponent(pRequest.Value)}`;
		const tmpRows = await this.getJSON(`${tmpDefinition.Entity}s/FilteredTo/${tmpFilter}/0/${this.maxRows}${tmpDeleted}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	/**
	 * @param {number} pIDUser
	 * @return {Promise<string|null>}
	 */
	async getUserName(pIDUser)
	{
		if (!pIDUser)
		{
			return null;
		}
		try
		{
			const tmpUser = await this.getJSON(`User/${encodeURIComponent(pIDUser)}?includeDeleted=true`);
			if (tmpUser && !tmpUser.Error && (tmpUser.NameFirst || tmpUser.NameLast))
			{
				return `${tmpUser.NameFirst || ''} ${tmpUser.NameLast || ''}`.trim();
			}
		}
		catch (pError)
		{
			return null;
		}
		return null;
	}
}

module.exports = RestEntitySource;
